import { useMemo } from 'react';
import { combineAccessInputs, mapAccessInput, type AccessInput } from '@/abilities/accessInput';
import type { DestinationAccess } from '@/abilities/destination';

/**
 * What a route waits for and whether it may open: a hook, created once when the route table loads, so a guard calls
 * the same one for its whole life. Like every hook of an access input, it returns the same object while its inputs
 * are unchanged.
 */
export type RouteCondition = () => AccessInput<boolean>;

export const allOf = (first: RouteCondition, second: RouteCondition): RouteCondition =>
  function useAllOf() {
    const a = first();
    const b = second();
    return useMemo(() => mapAccessInput(combineAccessInputs({ a, b }), (both) => both.a && both.b), [a, b]);
  };

/** Builds a case's route conditions from its access reader: a route opens when its destination is open. */
export const createDestinationRouteCondition =
  <Key extends string>(useAccess: () => AccessInput<{ readonly accessOf: (destination: Key) => DestinationAccess }>) =>
  (destination: Key): RouteCondition =>
    function useDestinationRoute() {
      const access = useAccess();
      return useMemo(() => mapAccessInput(access, ({ accessOf }) => accessOf(destination) === 'open'), [access]);
    };
